import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import FeedItem from '@/components/FeedItem';
import MobileNav from '@/components/MobileNav';
import CommentsSheet from '@/components/CommentsSheet';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LAST_VIEWED_KEY = 'casterflow_last_viewed';

const FeedPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const containerRef = useRef(null);
  const lastScrolledId = useRef(null);

  const fetchPosts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/feed`);
      setPosts(response.data);
    } catch (error) {
      toast.error('Failed to load feed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  useEffect(() => {
    if (posts.length === 0 || !containerRef.current) return;
    
    const targetId = searchParams.get('id');
    
    if (targetId && targetId !== lastScrolledId.current) {
      const targetIndex = posts.findIndex(p => p.id === targetId);
      if (targetIndex >= 0) {
        const targetElement = containerRef.current.children[targetIndex];
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth' });
          lastScrolledId.current = targetId;
        }
      }
    } else if (!targetId && !lastScrolledId.current) {
      const lastViewed = localStorage.getItem(LAST_VIEWED_KEY);
      if (lastViewed) {
        const targetIndex = posts.findIndex(p => p.id === lastViewed);
        if (targetIndex > 0) {
          const targetElement = containerRef.current.children[targetIndex];
          if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'auto' });
          }
        }
      }
      lastScrolledId.current = 'initial';
    }
  }, [posts, searchParams]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || posts.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const postId = entry.target.dataset.postId;
            if (postId) {
              localStorage.setItem(LAST_VIEWED_KEY, postId);
            }
          }
        });
      },
      { threshold: 0.5 }
    );

    Array.from(container.children).forEach((child) => {
      if (child.dataset.postId) {
        observer.observe(child);
      }
    });

    return () => observer.disconnect();
  }, [posts]);

  const handleCommentClick = (post) => {
    setSelectedPost(post);
    setCommentsOpen(true);
  };

  const handleCommentAdded = (postId) => {
    setPosts(prev => prev.map(p => 
      p.id === postId ? { ...p, comments_count: p.comments_count + 1 } : p
    ));
  };

  const handleLikeUpdate = (postId, liked) => {
    setPosts(prev => prev.map(p => 
      p.id === postId ? { 
        ...p, 
        is_liked: liked,
        likes_count: liked ? p.likes_count + 1 : p.likes_count - 1 
      } : p
    ));
  };

  const handleDelete = (postId) => {
    setPosts(prev => prev.filter(p => p.id !== postId));
  };

  if (loading) {
    return (
      <div className="h-screen w-full bg-zom-blue flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-zom-yellow border-t-transparent animate-spin" />
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="h-screen w-full bg-zom-blue flex flex-col" data-testid="feed-page">
        <div className="flex-1 flex items-center justify-center pt-20 pb-24">
          <div className="text-center p-6">
            <p className="text-zom-white font-heading text-xl tracking-wide">BRAK POSTÓW</p>
            <p className="text-zom-white/60 font-body text-sm mt-2">SPRAWDŹ PÓŹNIEJ</p>
          </div>
        </div>

        <MobileNav />
      </div>
    );
  }

  return (
    <div className="h-screen w-full bg-zom-blue flex flex-col" data-testid="feed-page">
      {/* Feed Container - with padding for top/bottom nav */}
      <div
        ref={containerRef}
        className="snap-container no-scrollbar w-full pt-16 pb-20"
        data-testid="feed-container"
      >
        {posts.map((post) => (
          <div key={post.id} data-post-id={post.id}>
            <FeedItem
              post={post}
              onCommentClick={handleCommentClick}
              onLikeUpdate={handleLikeUpdate}
              onDelete={handleDelete}
            />
          </div>
        ))}
      </div>

      <MobileNav />

      <CommentsSheet
        post={selectedPost}
        open={commentsOpen}
        onOpenChange={setCommentsOpen}
        onCommentAdded={handleCommentAdded}
      />
    </div>
  );
};

export default FeedPage;
